import {
  WF_ROLE_DATA_APPROVE,
  WF_ROLE_DATA_CEO,
  WF_ROLE_DATA_CHECKER,
  WF_ROLE_DATA_INPUT,
  WF_ROLE_FINAL_APPROVE,
} from "./myTasksRoleFilters";
import { getUserWorkflowRoles } from "./normalizeUserProfile";

export type NavItemLike = {
  title?: string;
  link?: string;
  enabled?: boolean;
  adminOnly?: boolean;
  children?: NavItemLike[];
};

const CEO_RESTRICTED_TITLES = new Set([
  "Workflows",
  "Cascade Strategy",
  "Workflow Admin",
  "Admin Panel",
]);

const CEO_MEA_GROUP = "__ROLE_WF_CEO";

export const CEO_HIDDEN_STRATEGY_TOOLBAR_TABS = new Set([
  "strategymap",
  "docs",
]);

/** Strategy toolbar tabs hidden for every user (page disabled). */
export const HIDDEN_STRATEGY_TOOLBAR_TABS = new Set(["initiatives"]);

export function userHasCeoWorkflowRole(
  profile?: { role?: string } | null,
  meaGroups?: string[] | null
): boolean {
  if (getUserWorkflowRoles(profile).includes(WF_ROLE_DATA_CEO)) {
    return true;
  }
  return (meaGroups || []).some(
    (group) => group.trim() === CEO_MEA_GROUP || group.trim() === WF_ROLE_DATA_CEO
  );
}

/** CEO-only users (no champion/approver/final-approver roles) get a reduced nav. */
export function shouldHideCeoRestrictedNav(
  profile?: { role?: string } | null,
  meaGroups?: string[] | null
): boolean {
  if (!userHasCeoWorkflowRole(profile, meaGroups)) return false;

  const roles = getUserWorkflowRoles(profile);
  const hasOtherWorkflowRole = roles.some(
    (role) =>
      role === WF_ROLE_DATA_INPUT ||
      role === WF_ROLE_DATA_CHECKER ||
      role === WF_ROLE_DATA_APPROVE ||
      role === WF_ROLE_FINAL_APPROVE
  );
  return !hasOtherWorkflowRole;
}

export function isCeoRestrictedNavItem(item: NavItemLike): boolean {
  const title = item.title || "";
  if (CEO_RESTRICTED_TITLES.has(title)) return true;

  const link = (item.link || "").toLowerCase();
  return (
    link.includes("/mytasks") ||
    link.includes("/cascade") ||
    link.includes("/workflowadmin") ||
    link.includes("/adminpanel") ||
    link.includes("/reporttemplates") ||
    link.includes("/executivedashboard")
  );
}

export function isWorkflowAdminNavItem(item: NavItemLike): boolean {
  return (
    item.title === "Workflow Admin" ||
    (item.link || "").toLowerCase().includes("workflowadmin")
  );
}

export function isAdminPanelNavItem(item: NavItemLike): boolean {
  return (
    item.title === "Admin Panel" ||
    (item.link || "").toLowerCase().includes("adminpanel")
  );
}

export function isAdminOnlyNavItem(item: NavItemLike): boolean {
  return (
    item.adminOnly === true ||
    isWorkflowAdminNavItem(item) ||
    isAdminPanelNavItem(item)
  );
}

export function filterNavItemsForUser<T extends NavItemLike>(
  items: T[] | undefined,
  options: {
    profile?: { role?: string; tenantAdmin?: boolean } | null;
    meaGroups?: string[] | null;
    hasAdminAccess: boolean;
  }
): T[] {
  if (!items?.length) return [];

  const hideCeoRestricted = shouldHideCeoRestrictedNav(
    options.profile,
    options.meaGroups
  );

  return items
    .filter((item) => {
      // Disabled nav items must not appear in the navbar at all.
      if (item.enabled === false) return false;

      if (hideCeoRestricted && isCeoRestrictedNavItem(item)) return false;

      if (isAdminOnlyNavItem(item)) {
        if (!options.profile) return false;
        if (!options.hasAdminAccess) return false;
      }

      return true;
    })
    .map((item) => ({
      ...item,
      children: filterNavItemsForUser(item.children as T[] | undefined, options),
    }));
}

export function filterStrategyToolbarTabs<T extends { viewName: string }>(
  tabs: T[],
  profile?: { role?: string } | null,
  meaGroups?: string[] | null
): T[] {
  const withoutDisabled = tabs.filter(
    (tab) => !HIDDEN_STRATEGY_TOOLBAR_TABS.has(tab.viewName)
  );
  if (!shouldHideCeoRestrictedNav(profile, meaGroups)) return withoutDisabled;
  return withoutDisabled.filter(
    (tab) => !CEO_HIDDEN_STRATEGY_TOOLBAR_TABS.has(tab.viewName)
  );
}
